import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { TrashIcon } from '@heroicons/react/24/outline';
import HeadingCp from '../components/HeadingCp';
import EstimateBar from '../components/EstimateBar';
import CurrencyFormat from '../components/CurrencyFormat';
import Badge from '../components/Badge';
import { fi } from 'date-fns/locale';
import { set } from 'date-fns';

const EstimateDetail = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { id, key } = useParams();
  const navigate = useNavigate();
  const [estimate, setEstimate] = useState('');
  const [markupService, setMarkupService] = useState('');
  const [results, setResults] = useState('');
  const [labor, setLabor] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCost, setTotalCost] = useState(0);
  const [totalHours, setTotalHours] = useState(0);
  const [fieldQty, setFieldQty] = useState(0);
  const [systemType, setSystemType] = useState('');

  const [finalPrice, setFinalPrice] = useState('');
  const [savingFinalPrice, setSavingFinalPrice] = useState(false);

const [alternativePrices, setAlternativePrices] = useState([]);
const [newAlternativePrice, setNewAlternativePrice] = useState('');
const [newAltDescription, setNewAltDescription] = useState('');
const [savingAlternativePrice, setSavingAlternativePrice] = useState(false);


  const [taxType, setTaxType] = useState(null);

  useEffect(() => {
    if (id) {
      fetchData(id);
      fetchFinalPrice();
      fetchAlternative();
    }
  }, [id, apiUrl, navigate]);

 

  const fetchData = async () => {
    try {
      const response = await axios.get(`${apiUrl}/best_crud.php`, {
        params: { 
                  estimate: id,
                  builder_id: key,
                  getDetails: true
                },
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.data[0] === 'success') {
        const e = response.data[1].estimate;
        setEstimate(e);
        setMarkupService(response.data[1].markup_service);
        setLabor(response.data[1].laborBreakdown || []);
        setTotalCost(response.data[1].total_labor_cost || 0);
        setTotalHours(response.data[1].total_hours || 0);
        setFieldQty(response.data[1].label_total_field_device_qty || 0);
        setResults(Object.entries(response.data[1].results || {}));
        setSystemType(response.data[1].system_type || '');
        setTaxType(e.tax_type ?? 1);
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session expired. Please login again.", {
          position: "top-center"
        });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
          position: "top-center"
        });
      }
    }
  };

  // Build laborItems from the dynamic laborBreakdown
  const laborItems = Object.entries(estimate.laborBreakdown || {}).map(
    ([categoryName, categoryData]) => ({
      name: categoryName,
      hours: categoryData.hours,
      cost: categoryData.cost,
      rate: categoryData.rate,
    })
  );

  const handleTaxTypeChange = async (newType) => {
    setTaxType(newType);
      const bodyData = {
        status: 'updateTaxType',
        data: { tax_type: newType,  estimate_id: id },
      };
      try {
        const response = await axios.post(`${apiUrl}/best_crud.php`, bodyData, {
          headers: { 'Content-Type': 'application/json' },
        });
        if (response.data.success) {
             fetchData(id);

        } else {
          toast.error(response.data.error || 'Failed updating.', {
            position: 'top-center',
          });
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
        navigate('/login');
        } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
        position: "top-center"
        });
        }
      }  
  }; 

  const fetchFinalPrice = async () => {
    try {
      const res = await axios.get(`${apiUrl}/best_crud.php`, {
        params: {
          estimateFinal: 1,
          estimate_id: id,
        }
      });
      if (res.data && typeof res.data[1] !== 'undefined') {
        setFinalPrice(res.data[1]);
      }
    } catch (e) {
      // Optionally handle error
    }
  };

  const fetchAlternative = async () => {
    try {
      const res = await axios.get(`${apiUrl}/best_crud.php`, {
        params: {
          estimateAlternative: 1,
          estimate_id: id,
        }
      });
      if (res.data && Array.isArray(res.data[1])) {
        setAlternativePrices(res.data[1]);
      } else {
        setAlternativePrices([]);
      }
    } catch (e) {
      setAlternativePrices([]);
    }
  };
  

  return (
    <div className="mx-auto max-w-[1200px] sm:px-6 lg:px-6 py-8 bg-white">
      <EstimateBar id={id} name={'Detail'} builder_id={key} />
      <HeadingCp label={"Estimate Details"} />
      <div className='mx-auto max-w-[1000px]'>

        <div className="sm:flex sm:items-center mb-8">
          <div className="sm:flex-auto">
            <p className="text-sm text-gray-700">
              Detailed breakdown of the estimate including services, labor, equipment, and subcontractor details.
            </p>
          </div>
        </div>

        <div className='mb-4'>
          <span className='text-xs bg-yellow-50 p-2 mb-2'>EstimateID:{estimate.id} | <Badge feed={estimate.building_type} /></span>
          <h3 className="text-base font-semibold leading-7 text-gray-900">
            {estimate.bidding_address || ''}
          </h3>
          <p className="max-w-2xl text-sm leading-6 text-gray-500">
            {estimate.contact_address || ''} {estimate.contact_address ? ',' : ''} 
            {estimate.contact_city || ''} 
            {estimate.contact_state || ''} 
            {estimate.contact_zip || ''}
          </p>
        </div>

        <div className='text-gray-500 border-b mb-6'>
          <p className='text-gray-800'>{systemType || ''}</p>
        </div>

        <div className='text-gray-700 border-b mb-6'>
          <p>{estimate.job_mark_up ? ('Mark Up: ' + estimate.job_mark_up + '%') : ('')}</p>
          <p>{estimate.sub_contract_mark_up ? ('Sub Mark Up: ' + estimate.sub_contract_mark_up + '%') : ('')}</p>
          <p>{estimate.notifier_discount ? ('Notifier Discount: ' + estimate.notifier_discount + '%') : ('')}</p>
          <p>{estimate.union_labor ? ('Labor: ' + estimate.union_labor) : ('')}</p>
        </div>

        {/* //services  */}
        {Array.isArray(markupService) && markupService.length > 0 && (
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 sm:px-3 p-6 max-w-[1000px]">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-400 border border-gray-200">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:px-3"
                    >
                      Service
                    </th>
                    <th
                      scope="col"
                      className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Amount
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 bg-white">
                  {markupService.map((row) => (
                    <tr key={row.id}>
                      <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:px-3 border border-gray-300 px-3">
                        {row.service} {/* e.g. "Filing Only" */}
                      </td>
                      <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                        <CurrencyFormat value={row.price} />
                      </td>
                    </tr>
                  ))}
                </tbody>

                <tfoot className="bg-slate-100">
                  <tr>
                    <td
                      colSpan="2"
                      className="whitespace-nowrap py-2 text-center text-green-600 border border-gray-300 px-3"
                    >
                      {/* Sum all prices */}
                      Total{" "}
                      <CurrencyFormat
                        value={markupService.reduce((acc, item) => acc + (item.price || 0), 0)}
                      />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* LABOR DETAILS */}
        <div className="pb-8 mt-10">
          <h4 className="font-bold p-2">Labor Details</h4>
          <div className="text-gray-500 border-b mb-6">
            <p className="text-gray-800">Total Hours: {Math.round(totalHours)}</p>
            <p className="text-gray-800">Total Field Devices: {fieldQty}</p>
          </div>

          <div className="pb-10">
            <h4 className="font-bold p-2">Labor Details</h4>
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 sm:px-3 p-3 max-w-[1000px]">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-400 border border-gray-200">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:px-3"
                      >
                        Description
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Hourly Rate
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Hours
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {/* Loop over the laborBreakdown object */}
                    {labor &&
                      Object.entries(labor).map(([desc, data], index) => (
                        <tr key={index}>
                          <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:px-3 border border-gray-300 px-3">
                            {desc}
                          </td>
                          <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                            <CurrencyFormat value={parseFloat(data.rate)} />
                          </td>
                          <td className="whitespace-nowrap py-2 text-sm text-gray-500 border border-gray-300 px-3">
                            {(Number(data.hours) || 0).toFixed(2)}
                          </td>
                          <td className="whitespace-nowrap py-2 text-sm text-gray-500 border border-gray-300 px-3">
                            <CurrencyFormat value={data.cost} />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                  <tfoot className="bg-slate-100">
                    <tr>
                      <td
                        colSpan="4"
                        className="whitespace-nowrap py-2 text-center text-green-600 border border-gray-300 px-3"
                      >
                        <b>
                          Total <CurrencyFormat value={totalCost} />
                        </b>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>

        {estimate.totalFCS && (
          <>
            {/* EQUIPMENT DETAILS */}
            <div className="pb-10">
              <h4 className="font-bold p-2">Equipment Details</h4>
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 sm:px-3 p-6 max-w-[1000px]">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-gray-400 border border-gray-200">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          className="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:px-3"
                        >
                          Equipment
                        </th>
                        <th
                          scope="col"
                          className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      <tr>
                        <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:px-3 border border-gray-300 px-3">
                          FCS /FAPT COMPONENTS
                        </td>
                        <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                          <CurrencyFormat value={estimate.totalFCS} />
                        </td>
                      </tr>
                      <tr>
                        <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:pr-3 border border-gray-300 px-3">
                          FIELD DEVICES COST
                        </td>
                        <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                          <CurrencyFormat value={estimate.totalFDEVICE} />
                        </td>
                      </tr>
                      <tr>
                        <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:px-3 border border-gray-300 px-3">
                          DATA GATHERING PANEL COST
                        </td>
                        <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                          <CurrencyFormat value={estimate.totalDGP} />
                        </td>
                      </tr>
                      <tr>
                        <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:px-3 border border-gray-300 px-3">
                          EQUIPMENT COST - NO DISCOUNT
                        </td>
                        <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                          <CurrencyFormat value={estimate.totalNoDiscount} />
                        </td>
                      </tr>
                      <tr>
                        <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:px-3 border border-gray-300 px-3">
                          EQUIPMENT COST WITH DISCOUNT OF {estimate.notifier_discount}%
                        </td>
                        <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                          <CurrencyFormat value={estimate.totalDiscount} />
                        </td>
                      </tr>
                    </tbody>

                    <tfoot className="bg-slate-100">
                      <tr>
                        <td
                          colSpan="2"
                          className="whitespace-nowrap py-2 text-center text-green-600 border border-gray-300 px-3"
                        >
                          <b>
                            Total{" "}
                            <CurrencyFormat value={estimate.totalEquipment} />{" "}
                            <span>{estimate.totalDiscount > 0 && "With Discount"}</span>
                          </b>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {/* SUB-CONTRACTOR */}
        {estimate.totalCostContractor && (
          <div className="pb-10">
            <h4 className='font-bold p-2'>Sub-Contractor Details</h4>
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 sm:px-3 p-6 max-w-[1000px]">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-400 border border-gray-200">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:px-3"
                      >
                        Contractor
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    <tr>
                      <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:px-3 border border-gray-300 px-3">
                        HVAC CONTRACTOR
                      </td>
                      <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                        <CurrencyFormat value={estimate.elecContractor.hvac} />
                      </td>
                    </tr>
                    <tr>
                      <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:px-3 border border-gray-300 px-3">
                        SPRINKLER CONTRACTOR
                      </td>
                      <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                        <CurrencyFormat value={estimate.elecContractor.sprinkler} />
                      </td>
                    </tr>
                    <tr>
                      <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:px-3 border border-gray-300 px-3">
                        SECURITY CONTRACTOR
                      </td>
                      <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                        <CurrencyFormat value={estimate.elecContractor.security} />
                      </td>
                    </tr>
                    <tr>
                      <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:px-3 border border-gray-300 px-3">
                        GENERAL CONTRACTOR
                      </td>
                      <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                        <CurrencyFormat value={estimate.elecContractor.general} />
                      </td>
                    </tr>
                    <tr>
                      <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:px-3 border border-gray-300 px-3">
                        ELEVATOR CONTRACTOR
                      </td>
                      <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                        <CurrencyFormat value={estimate.elecContractor.elevator} />
                      </td>
                    </tr>
                  </tbody>
                  <tfoot className="bg-slate-100">
                    <tr>
                      <td colSpan="2" className="whitespace-nowrap py-2 text-center text-green-600 border border-gray-300 px-3">
                        <b>Total <CurrencyFormat value={estimate.totalCostContractor} /></b>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* WIRE */}
        {estimate.es_wire && (
          <div className="pb-10">
            <h4 className='font-bold p-2'>Wire Details</h4>
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 sm:px-3 p-6 max-w-[1000px]">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-400 border border-gray-200">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:px-3"
                      >
                        Wire
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Total Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    <tr>
                      <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:px-3 border border-gray-300 px-3">
                        WIRE #14 1PR
                      </td>
                      <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                        <CurrencyFormat value={estimate.es_wire.w14_1pr * 1150} />
                      </td>
                    </tr>
                    <tr>
                      <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:px-3 border border-gray-300 px-3">
                        WIRE #14 2PR
                      </td>
                      <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                        <CurrencyFormat value={estimate.es_wire.w14_2pr * 1725} />
                      </td>
                    </tr>
                    <tr>
                      <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:px-3 border border-gray-300 px-3">
                        WIRE #16 1PR
                      </td>
                      <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                        <CurrencyFormat value={estimate.es_wire.w16_1pr * 1150} />
                      </td>
                    </tr>
                    <tr>
                      <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:px-3 border border-gray-300 px-3">
                        WIRE #16 2PR
                      </td>
                      <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                        <CurrencyFormat value={estimate.es_wire.w16_2pr * 1725} />
                      </td>
                    </tr>
                    <tr>
                      <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:px-3 border border-gray-300 px-3">
                        WIRE CI CABLE
                      </td>
                      <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                        <CurrencyFormat value={estimate.es_wire.ci_cable * 3450} />
                      </td>
                    </tr>
                  </tbody>
                  <tfoot className='bg-slate-100'>
                    <tr>
                      <td colSpan="2" className="whitespace-nowrap py-2 text-center text-green-600 border border-gray-300 px-3">
                        <b>Total <CurrencyFormat value={estimate.wireSectionCost} /></b>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* MISCELLANEOUS */}
        {estimate.total_cost_misc && (
          <div className="pb-10">
            <h4 className='font-bold p-2'>Miscellaneous Details</h4>
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 sm:px-3 p-6 max-w-[1000px]">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-400 border border-gray-200">
                  <thead>
                    <tr>
                      <th scope="col"
                          className="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:px-3">
                        Description
                      </th>
                      <th scope="col"
                          className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Total Qty
                      </th>
                      <th scope="col"
                          className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Total Price
                      </th>
                      <th scope="col"
                          className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Total Cost
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 bg-white">
                    {estimate.misc_section.map((row, index) => (
                      <tr key={index}>
                        <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:px-3 border border-gray-300 px-3">
                          {row.description}
                        </td>
                        <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                          {row.quantity.toFixed(2)}
                        </td>
                        <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                          <CurrencyFormat value={row.price}/>
                        </td>
                        <td className="whitespace-nowrap py-2 text-sm font-medium text-gray-900 border border-gray-300 px-3">
                          <CurrencyFormat value={(row.price * row.quantity)}/>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className='bg-slate-100'>
                    <tr>
                      <td colSpan="4" className="whitespace-nowrap py-2 text-center text-green-600 border border-gray-300 px-3">
                        <b>Total <CurrencyFormat value={estimate.total_cost_misc}/></b>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAX TYPE (no save button needed) */}
        <div className="my-6 border-b pb-4">
          <h4 className="font-semibold mb-2">Tax Type</h4>
          <div className="flex flex-wrap gap-6">
            {[
              { label: 'Tax Full',           value: 1 },
              { label: 'Tax Exempt',         value: 2 },
              { label: 'Tax Labor Only',     value: 3 },
              { label: 'Tax Equipment Only', value: 4 },
            ].map(opt => (
              <label key={opt.value} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="tax_type"
                  value={opt.value}
                  checked={estimate.tax_type === opt.value}
                  onChange={() => handleTaxTypeChange(opt.value)}
                  className="h-4 w-4 text-indigo-600 border-gray-300"
                />
                <span className="text-sm text-gray-700">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>       

        {/* RESULTS */}
        {results && results.length > 0 ? (
          <div className="pb-10 text-sm">
            <div className="container">
              <div className="row">
                {results.map(([key, value], index) => (
                  <div key={index} className="flex justify-between py-2 border-b border-gray-300">
                    {value === 0 ? (
                      <>
                        <span className='pr-8 opacity-40'>
                          {key}
                        </span>
                          <b className="text-red-600 opacity-40">
                            <CurrencyFormat value={value}/>
                          </b>
                      </>
                    ) : (
                      <>
                        <span className='pr-8'>
                          {key}
                        </span>
                          <b className="text-green-600">
                            <CurrencyFormat value={value}/>
                          </b>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {/* FINAL PRICE */}
        <div className="mb-10 border-b pb-4 max-w-md">
          <h4 className="font-semibold mb-2">Final Price</h4>
          <div className="flex items-center space-x-3">
            <input
              type="number"
              min="0"
              step="0.01"
              className="border rounded px-2 py-1 w-40"
              value={finalPrice === null || typeof finalPrice === 'undefined' ? '' : finalPrice}
              onChange={e => setFinalPrice(e.target.value)}
              disabled={savingFinalPrice}
            />
            <button
              className="px-3 py-1 rounded bg-green-600 text-white font-bold hover:bg-green-700"
              onClick={async () => {
                setSavingFinalPrice(true);
                try {
                  const body = {
                    status: 'saveFinalPrice',
                    data: { estimate_id: id, final_price: parseFloat(finalPrice) }
                  };
                  const res = await axios.post(`${apiUrl}/best_crud.php`, body, {
                    headers: { 'Content-Type': 'application/json' }
                  });
                  if (res.data && res.data.success) {
                    toast.success('Final price saved!', { position: 'top-center' });
                    fetchFinalPrice(); // reload after save if you want
                  } else {
                    toast.error('Failed to save final price.', { position: 'top-center' });
                  }
                } catch (err) {
                  toast.error('Error saving final price.', { position: 'top-center' });
                } finally {
                  setSavingFinalPrice(false);
                }
              }}
              disabled={savingFinalPrice}
            >
              Save
            </button>
          </div>
          <span className='text-xs ml-1 font-bold italic'>
            {finalPrice ? <CurrencyFormat value={finalPrice} /> : ''}
          </span>
        </div>     

        {/* Alternative price */}
        <div className="mb-10 border-b pb-4 max-w-full">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold">Alternative Prices</h4>
          </div>

        {/* existing alternatives */}
        {alternativePrices.length > 0 && (
          <div className="space-y-2 mb-4">
            {alternativePrices.map((alt) => (
              <div
                key={alt.id}
                className="flex items-center gap-3 border rounded p-3 bg-gray-50"
              >
                <div className="w-40 font-semibold text-green-700">
                  <CurrencyFormat value={alt.alternative_price} />
                </div>

                <div className="flex-1 text-sm text-gray-700">
                  {alt.description || 'No description'}
                </div>

                <button
                  className="px-3 py-1 rounded bg-red-600 text-white font-bold hover:bg-red-700"
                  onClick={async () => {
                    try {
                      setSavingAlternativePrice(true);
                      const body = {
                        status: 'deleteAlternatePrice',
                        data: { id: alt.id }
                      };
                      const res = await axios.post(`${apiUrl}/best_crud.php`, body, {
                        headers: { 'Content-Type': 'application/json' },
                      });
                      if (res.data && res.data.success) {
                        toast.success('Alternative price deleted!', { position: 'top-center' });
                        fetchAlternative();
                      } else {
                        toast.error('Failed to delete alternative price.', { position: 'top-center' });
                      }
                    } catch (err) {
                      toast.error('Error deleting alternative price.', { position: 'top-center' });
                    } finally {
                      setSavingAlternativePrice(false);
                    }
                  }}
                  disabled={savingAlternativePrice}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}

        {/* add new alternative */}
        <div className="flex items-center space-x-3">
          <input
            type="number"
            min="0"
            step="0.01"
            className="border rounded px-2 py-1 w-40"
            value={newAlternativePrice}
            onChange={e => setNewAlternativePrice(e.target.value)}
            disabled={savingAlternativePrice}
            placeholder="Amount"
          />

          <input
            type="text"
            className="border rounded px-2 py-1 w-1/2"
            placeholder="Description"
            value={newAltDescription}
            onChange={e => setNewAltDescription(e.target.value)}
            disabled={savingAlternativePrice}
          />

          <button
            className="px-3 py-1 rounded bg-blue-600 text-white font-bold hover:bg-blue-700"
            onClick={async () => {
              if (!newAlternativePrice || parseFloat(newAlternativePrice) <= 0) {
                toast.error('Enter a valid alternative price.', { position: 'top-center' });
                return;
              }

              try {
                setSavingAlternativePrice(true);

                const body = {
                  status: 'saveAlternatePrice',
                  data: {
                    estimate_id: id,
                    alternate_price: parseFloat(newAlternativePrice),
                    description: newAltDescription || ''
                  }
                };

                const res = await axios.post(`${apiUrl}/best_crud.php`, body, {
                  headers: { 'Content-Type': 'application/json' },
                });

                if (res.data && res.data.success) {
                  toast.success('Alternative price added!', { position: 'top-center' });
                  setNewAlternativePrice('');
                  setNewAltDescription('');
                  fetchAlternative();
                } else {
                  toast.error('Failed to save alternative price.', { position: 'top-center' });
                }
              } catch (err) {
                toast.error('Error saving alternative price.', { position: 'top-center' });
              } finally {
                setSavingAlternativePrice(false);
              }
            }}
            disabled={savingAlternativePrice}
          >
            Add
          </button>
        </div>
        </div>     

      </div>
    </div>
  );
};

export default EstimateDetail;
